import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import { usePatchCategory } from 'actions/chatbot';

import {
  FormContainer,
  FormHeader,
  FormTitle,
  PathText,
  CategoryBadge,
  CategoryHelpText,
  Field,
  Label,
  Input,
  ButtonRow,
  SaveButton,
} from './styled-comps';

function CategoryForm({ category, onSaved }) {
  const dispatch = useDispatch();
  const [frt, setFrt] = useState('');
  const [sla, setSla] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!category) return;
    setFrt(category.frt_seconds == null ? '' : String(category.frt_seconds));
    setSla(category.sla_seconds == null ? '' : String(category.sla_seconds));
  }, [category]);

  const { patch: patchCategory } = usePatchCategory({
    onComplete: errorMessage => {
      setSaving(false);
      if (errorMessage) {
        dispatch(
          showNotification({
            text: `Failed to save: ${errorMessage}`,
            duration: 5,
            showCloseIcon: true,
          }),
        );
        return;
      }
      dispatch(
        showNotification({
          text: 'Category updated',
          duration: 3,
          showCloseIcon: true,
        }),
      );
      onSaved && onSaved();
    },
  });

  if (!category) return null;

  const parseSeconds = value => {
    if (value === '' || value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  };

  const handleSave = () => {
    if (frt === '' && sla === '') return;

    const frtValue = parseSeconds(frt);
    const slaValue = parseSeconds(sla);
    if (frt !== '' && frtValue === null) {
      dispatch(
        showNotification({
          text: 'First response time must be a positive integer',
          duration: 5,
          showCloseIcon: true,
        }),
      );
      return;
    }
    if (sla !== '' && slaValue === null) {
      dispatch(
        showNotification({
          text: 'Resolution time must be a positive integer',
          duration: 5,
          showCloseIcon: true,
        }),
      );
      return;
    }
    setSaving(true);
    patchCategory(category.path, {
      frt_seconds: frtValue,
      sla_seconds: slaValue,
    });
  };

  return (
    <FormContainer>
      <FormHeader>
        <FormTitle>{category.display_name}</FormTitle>
        <CategoryBadge>Category</CategoryBadge>
      </FormHeader>
      <PathText>{category.path}</PathText>
      <CategoryHelpText>
        Double-click the category name in the tree to rename it. SLA timings below apply
        to tickets assigned under this category.
      </CategoryHelpText>

      <Field>
        <Label>First response time (seconds)</Label>
        <Input
          type="number"
          min="1"
          step="1"
          value={frt}
          placeholder="e.g. 300 (5 min)"
          onChange={e => setFrt(e.target.value)}
        />
      </Field>

      <Field>
        <Label>Resolution time (seconds)</Label>
        <Input
          type="number"
          min="1"
          step="1"
          value={sla}
          placeholder="e.g. 86400 (24 h)"
          onChange={e => setSla(e.target.value)}
        />
      </Field>

      <ButtonRow>
        <SaveButton
          onClick={handleSave}
          disabled={saving}
          label={saving ? 'Saving...' : 'Save'}
        />
      </ButtonRow>
    </FormContainer>
  );
}

export default CategoryForm;
